'use client';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Header, Onboarding, toast, useModal } from '@ohif/ui-next';
import { SidebarNavigation } from '@ohif/ui';
import { useAppConfig } from '@state';
import PropTypes from 'prop-types';

// Importaciones del servicio de Modalidades
import {
  fetchAllModalities,
  createModality,
  updateModality,
  deleteModality,
  ModalityAPI,
  ModalityData,
} from '../Services/services';
import { getHeaderConfig } from '../Users/Users';
import ModalityFormModal from './components/ModalityFormModal';
import ModalityList from './components/ModalityList';

// --- Estado Inicial del Formulario ---
const INITIAL_FORM_DATA: ModalityData = {
  name: '',
  dicom_code: '',
  description: '',
  status: true,
};

function ModalityPanel({ servicesManager }) {
  const [modalities, setModalities] = useState<ModalityAPI[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingModality, setEditingModality] = useState<ModalityAPI | null>(null);
  const [formData, setFormData] = useState<ModalityData>(INITIAL_FORM_DATA);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const { t } = useTranslation();
  const { show } = useModal();
  const [appConfig] = useAppConfig();
  const { menuOptions } = getHeaderConfig({ servicesManager, t, show, navigate, appConfig });

  // --- Lógica de Carga de Datos ---
  const loadModalities = async () => {
    try {
      setIsLoading(true);
      const apiModalities = await fetchAllModalities();
      setModalities(apiModalities);
    } catch (error) {
      toast.error('Error al obtener modalidades: ' + (error as any).message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadModalities();
  }, []);

  // --- Lógica de Filtro ---
  const filteredModalities = modalities.filter(
    (mod) =>
      mod.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      mod.dicom_code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // --- Manejadores CRUD ---
  const handleCreateModality = async () => {
    try {
      const response = await createModality(formData);
      toast.success(`Modalidad ${response.name} (${response.dicom_code}) creada exitosamente.`);
      await loadModalities();
      resetForm();
    } catch (err: any) {
      toast.error('Error al crear modalidad: ' + (err.message || err));
    }
  };

  const handleUpdateModality = async () => {
    if (!editingModality) return;
    try {
      // Nota: El backend debería manejar la prevención de cambios en dicom_code si es clave.
      const response = await updateModality(editingModality._id, formData);
      toast.success(`Modalidad ${response.name} actualizada.`);
      await loadModalities();
      resetForm();
    } catch (err: any) {
      toast.error('Error al actualizar modalidad: ' + (err.message || err));
    }
  };

  const handleDeleteModality = async (id: string) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta modalidad?')) return;
    try {
      await deleteModality(id);
      toast.success('Modalidad eliminada correctamente.');
      setModalities((prev) => prev.filter((m) => m._id !== id));
    } catch (err: any) {
      toast.error('Error al eliminar la modalidad: ' + (err.message || err));
    }
  };

  const handleEditModality = (modality: ModalityAPI) => {
    setEditingModality(modality);
    setFormData({
      name: modality.name,
      dicom_code: modality.dicom_code,
      description: modality.description || '',
      status: modality.status,
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData(INITIAL_FORM_DATA);
    setEditingModality(null);
    setIsDialogOpen(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    editingModality ? handleUpdateModality() : handleCreateModality();
  };

  return (
    <div className="flex h-full flex-col bg-black">
      <Header
        isSticky
        menuOptions={menuOptions}
        isReturnEnabled={false}
        WhiteLabeling={appConfig.whiteLabeling}
      />
      <Onboarding />
      <SidebarNavigation onFilterToggle={() => navigate('/')} />

      <div className="container mx-auto min-h-screen bg-gray-50 pt-5 text-white max-sm:p-4 sm:pl-9">
        {/* Header del Panel */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Gestión de Modalidades</h1>
            <p className="mt-1">Define los tipos de estudio (CT, MR, US, etc.)</p>
          </div>
          <button
            onClick={() => {
              setEditingModality(null);
              setFormData(INITIAL_FORM_DATA);
              setIsDialogOpen(true);
            }}
            className="bg-secondary-dark hover:bg-primary-dark flex items-center rounded px-4 py-2 text-white"
          >
            Crear Modalidad
          </button>
        </div>

        {/* Lista de Modalidades */}
        <ModalityList
          modalities={filteredModalities}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          isLoading={isLoading}
          handleEditModality={handleEditModality}
          handleDeleteModality={handleDeleteModality}
        />
      </div>

      {/* Modal de Formulario */}
      <ModalityFormModal
        isDialogOpen={isDialogOpen}
        editingModality={editingModality}
        formData={formData}
        setFormData={setFormData}
        handleSubmit={handleSubmit}
        resetForm={resetForm}
      />
    </div>
  );
}

ModalityPanel.propTypes = {
  servicesManager: PropTypes.object.isRequired,
};

export default ModalityPanel;
